import React from 'react';
import { Heading, Box, Text, Form, SubmitButton, CircularProgress } from '@medely/ui-kit';
import { SchedulingPreferencesFormFields } from './components/SchedulingPreferencesFormFields';
import { FixedFooterLayout } from '../FixedFooterLayout';
import {
  useSchedulingPreferencesForm,
  type SchedulingPreferencesFormValues,
} from './hooks/useSchedulingPreferencesForm';
import { schedulingPreferencesSchema } from './schema';

type SchedulingPreferencesFormProps = {
  onSuccess?: () => void;
};

export const SchedulingPreferencesForm = ({ onSuccess }: SchedulingPreferencesFormProps = {}) => {
  const {
    isLoading,
    isSubmitting,
    defaultValues,
    availableLabelOptions,
    positionOptions,
    showShiftPreferences,
    showDayPreferences,
    showFrequencyPreferences,
    showPositionPreferences,
    lastUpdatedText,
    handleSubmit,
  } = useSchedulingPreferencesForm({ onSuccess });

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Form
        handleSubmit={(values: SchedulingPreferencesFormValues) =>
          handleSubmit(values, defaultValues)
        }
        schema={schedulingPreferencesSchema}
        defaultValues={defaultValues}
      >
        <FixedFooterLayout
          footerContent={
            <SubmitButton fullWidth loading={isSubmitting} disabled={isSubmitting}>
              Save
            </SubmitButton>
          }
          height="98%"
        >
          <Heading size="xs">Scheduling Preferences</Heading>
          <Box pt={1} pb={2}>
            <Text size="m" color="text.secondary">
              Set your preferences to help us best match you with shifts.
            </Text>
          </Box>
          <Text size="m" color="text.secondary" italic>
            Preferences are not guaranteed and may take time to update in your schedule.
          </Text>
          <SchedulingPreferencesFormFields
            availableLabelOptions={availableLabelOptions}
            positionOptions={positionOptions}
            showShiftPreferences={showShiftPreferences}
            showDayPreferences={showDayPreferences}
            showFrequencyPreferences={showFrequencyPreferences}
            showPositionPreferences={showPositionPreferences}
          />
          {lastUpdatedText && (
            <Box py={2}>
              <Text size="s" color="text.secondary">
                {lastUpdatedText}
              </Text>
            </Box>
          )}
        </FixedFooterLayout>
      </Form>
    </Box>
  );
};
